import { useCallback, useEffect, useRef, useState } from 'react';

export type UndoKind = 'cell' | 'row' | 'column' | 'header' | 'sort' | 'convert' | 'paste';

export interface UndoEntry {
  kind: UndoKind;
  before: string;
  after: string;
  timestamp: number;
}

interface UseUndoStackOptions {
  value: string;
  onApply?: (nextValue: string) => void;
  maxDepth?: number;
}

export function useUndoStack({ value, onApply, maxDepth = 50 }: UseUndoStackOptions) {
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [redoStack, setRedoStack] = useState<UndoEntry[]>([]);
  const pendingValueRef = useRef<string | null>(null);
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (pendingValueRef.current !== null) {
      if (value === pendingValueRef.current) {
        pendingValueRef.current = null;
        lastValueRef.current = value;
        return;
      }
      pendingValueRef.current = null;
    }

    if (value !== lastValueRef.current) {
      setUndoStack([]);
      setRedoStack([]);
      lastValueRef.current = value;
    }
  }, [value]);

  const recordChange = useCallback((entry: Omit<UndoEntry, 'timestamp'>) => {
    if (entry.before === entry.after) return;
    setUndoStack((prev) => {
      const next = [...prev, { ...entry, timestamp: Date.now() }];
      return next.slice(-maxDepth);
    });
    setRedoStack([]);
    pendingValueRef.current = entry.after;
    lastValueRef.current = entry.after;
  }, [maxDepth]);

  const applyValue = useCallback((nextValue: string) => {
    if (!onApply) return;
    pendingValueRef.current = nextValue;
    onApply(nextValue);
  }, [onApply]);

  const undo = useCallback(() => {
    if (!onApply) return;
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const entry = prev[prev.length - 1];
      setRedoStack((redoPrev) => [...redoPrev, entry].slice(-maxDepth));
      applyValue(entry.before);
      return prev.slice(0, -1);
    });
  }, [applyValue, maxDepth, onApply]);

  const redo = useCallback(() => {
    if (!onApply) return;
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const entry = prev[prev.length - 1];
      setUndoStack((undoPrev) => [...undoPrev, entry].slice(-maxDepth));
      applyValue(entry.after);
      return prev.slice(0, -1);
    });
  }, [applyValue, maxDepth, onApply]);

  return {
    recordChange,
    undo,
    redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
  };
}
